import React, { useMemo } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Text,
    Button,
    HStack,
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    Select,
    Tooltip,
    Icon,
} from '@chakra-ui/react';
import { TbFileCheck, TbReceiptRefund } from 'react-icons/tb';

import type { ReturnSlipSubmitPreviewResult } from '../../utils/returnSlipSubmitPreview';
import type { ManualPoLineInfo } from '../../utils/nexSubmitManualPo';

const ACCENT = '#0071E3';
const ACCENT_HOVER = '#3544a0';

const AP_HEADER_INVOICE_TYPE_CHIP: Record<
    InvoiceSubmitPayloadPreview['apHeader']['invoiceType'],
    {
        label: string;
        icon: any;
        borderColor: string;
        bg: string;
        color: string;
    }
> = {
    REG: {
        label: 'Received',
        icon: TbFileCheck,
        borderColor: 'blue.500',
        bg: 'blue.50',
        color: 'blue.700',
    },
    CM: {
        label: 'Credited',
        icon: TbReceiptRefund,
        borderColor: 'red.500',
        bg: 'red.100',
        color: 'red.800',
    },
};

export interface InvoiceSubmitPayloadPreview {
    apHeader: {
        invoiceNo: string;
        invoiceDate: string;
        invoiceAmount: number;
        freight: number;
        discount: number;
        tax: number;
        gstHSTTax?: number;
        pstTax?: number;
        total: number;
        invoiceType: 'REG' | 'CM';
    };
    apDist: Array<{
        globalIndex: number;
        folderNo: number;
        poHeaderID: number | null;
        poLineNo: number | null;
        description: string;
        unitList: number;
        unitCost: number;
        units: number;
        totalCost: number;
        /** `0` = matched, `1` = manual/unmatched, `2` = reserved. */
        manualLineInd: 0 | 1 | 2;
    }>;
    submitBlocked?: boolean;
    manualPoLineInfos?: ManualPoLineInfo[];
    /** Billable rows submitting without a NEX PO line match. */
    unmatchedManualLineCount?: number;
}

export interface InvoiceSubmitConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    isLoading: boolean;
    onConfirm: () => void;
    payloadPreview?: InvoiceSubmitPayloadPreview | null;
    /** When set, show NEX return slip PUT preview instead of AP invoice payload. */
    returnSlipPreview?: ReturnSlipSubmitPreviewResult | null;
    /** True when NEX PO header is missing for any distribution line — block submit. */
    submitBlocked?: boolean;
    /** Manual orphan lines: eligible POs and metadata (keyed by globalIndex in table). */
    manualPoLineInfos?: ManualPoLineInfo[];
    manualPoHeaderSelections?: Record<number, number>;
    onManualPoChange?: (globalIndex: number, poHeaderId: number) => void;
    /** Org setting: when true, freight is represented as a line item/tab and should be hidden from AP header summary. */
    freightAsLineItem?: boolean;
    /** Org setting: when true, US sales tax is an AP dist line and should be hidden from AP header summary. */
    salesTaxAsLineItem?: boolean;
    unmatchedManualLineCount?: number;
}

const InvoiceSubmitConfirmModal = ({
    isOpen,
    onClose,
    isLoading,
    onConfirm,
    payloadPreview,
    returnSlipPreview,
    submitBlocked = false,
    manualPoLineInfos = [],
    manualPoHeaderSelections = {},
    onManualPoChange = () => {},
    freightAsLineItem = true,
    salesTaxAsLineItem = false,
    unmatchedManualLineCount = 0,
}: InvoiceSubmitConfirmModalProps) => {
    const fmtMoney = (n: number): string =>
        `$${(Number.isFinite(n) ? n : 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const fmtUnitMoney = (n: number): string =>
        `$${(Number.isFinite(n) ? n : 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;

    const manualPoByGlobalIndex = useMemo(() => {
        const m = new Map<number, ManualPoLineInfo>();
        for (const row of manualPoLineInfos) {
            m.set(row.globalIndex, row);
        }
        return m;
    }, [manualPoLineInfos]);

    const manualPoSelectionIncomplete =
        manualPoLineInfos.length > 0 &&
        manualPoLineInfos.some((row) => {
            const sel = manualPoHeaderSelections[row.globalIndex];
            return !Number.isFinite(sel) || (typeof sel === 'number' && sel <= 0);
        });

    const confirmDisabled = isLoading || submitBlocked || manualPoSelectionIncomplete;

    const submitDisabledTooltip = useMemo((): string | undefined => {
        if (!confirmDisabled) return undefined;
        if (isLoading) return 'Submitting…';
        if (manualPoSelectionIncomplete) {
            return 'For each manual line (including restocking/misc fees), choose a PO header in the distribution table.';
        }
        if (submitBlocked) {
            return 'One or more distribution lines are missing a required PO mapping and cannot be submitted yet.';
        }
        return undefined;
    }, [confirmDisabled, isLoading, manualPoSelectionIncomplete, submitBlocked]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl">
            <ModalOverlay />
            <ModalContent borderRadius="xl" overflow="hidden">
                <ModalHeader fontSize="xl" fontWeight="bold" color="gray.900">
                    {returnSlipPreview != null ? 'Submit return slip to NEX?' : 'Submit invoice?'}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={4}>
                    <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3}>
                        <Text fontSize="md" color="orange.700" fontWeight="medium">
                            {returnSlipPreview != null
                                ? 'This will call NEX PUT /purchasing/returnslip with your matched PO lines and the invoice image URL, then mark the return slip as submitted (view-only).'
                                : 'This will mark the invoice as manually submitted. After submitting, the invoice will become view-only and can no longer be edited. Submit when you have reviewed the invoice and are ready to finalize.'}
                        </Text>
                    </Box>
                    {returnSlipPreview?.ok && (
                        <>
                            <Box mt={5}>
                                <Text fontSize="sm" fontWeight="semibold" color="gray.800" mb={2}>
                                    Return slip PUT payload (one request per PO header)
                                </Text>
                                <Text fontSize="xs" color="gray.500" mb={2}>
                                    imageURL: set at submit time from your stored invoice image (presigned URL).
                                </Text>
                                {returnSlipPreview.requests.map((req, idx) => (
                                    <Box
                                        key={`${req.poHeaderID}-${idx}`}
                                        mb={4}
                                        p={3}
                                        border="1px solid"
                                        borderColor="gray.200"
                                        borderRadius="md"
                                        bg="gray.50"
                                    >
                                        {returnSlipPreview.requests.length > 1 && (
                                            <Text fontSize="xs" fontWeight="semibold" color="gray.700" mb={2}>
                                                NEX PUT request {idx + 1} of {returnSlipPreview.requests.length}
                                            </Text>
                                        )}
                                        <Text fontSize="xs" fontFamily="mono" whiteSpace="pre-wrap">
                                            {JSON.stringify(
                                                {
                                                    folderNo: req.folderNo,
                                                    poHeaderID: req.poHeaderID,
                                                    poLines: req.poLines,
                                                    imageURL: '(presigned URL at submit)',
                                                },
                                                null,
                                                2
                                            )}
                                        </Text>
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}
                    {!returnSlipPreview && payloadPreview && unmatchedManualLineCount > 0 && (
                        <Box mt={4} bg="yellow.50" border="1px solid" borderColor="yellow.300" borderRadius="md" p={3}>
                            <Text fontSize="sm" color="yellow.900" fontWeight="medium">
                                {unmatchedManualLineCount === 1
                                    ? '1 line item does not match a NEX PO line and will be submitted as a manual line.'
                                    : `${unmatchedManualLineCount} line items do not match NEX PO lines and will be submitted as manual lines.`}
                            </Text>
                           
                        </Box>
                    )}
                    {!returnSlipPreview && payloadPreview && (
                        <>
                            <Box mt={5}>
                                <Text fontSize="sm" fontWeight="semibold" color="gray.800" mb={2}>
                                    AP Header values to submit
                                </Text>
                                <Box overflowX="auto">
                                <Table size="sm" variant="simple" border="1px solid" borderColor="gray.200" borderRadius="md">
                                    <Thead bg="gray.50">
                                        <Tr>
                                            <Th textTransform="uppercase" color="gray.500" fontSize="xs" letterSpacing="wider">
                                                Invoice No
                                            </Th>
                                            <Th textTransform="uppercase" color="gray.500" fontSize="xs" letterSpacing="wider">
                                                Invoice Date
                                            </Th>
                                            <Th textTransform="uppercase" color="gray.500" fontSize="xs" letterSpacing="wider">
                                                Invoice Type
                                            </Th>
                                            <Th
                                                textTransform="uppercase"
                                                color="gray.500"
                                                fontSize="xs"
                                                letterSpacing="wider"
                                                isNumeric
                                            >
                                                Invoice Amount
                                            </Th>
                                            {!freightAsLineItem && (
                                                <Th
                                                    textTransform="uppercase"
                                                    color="gray.500"
                                                    fontSize="xs"
                                                    letterSpacing="wider"
                                                    isNumeric
                                                >
                                                    Freight
                                                </Th>
                                            )}
                                            <Th
                                                textTransform="uppercase"
                                                color="gray.500"
                                                fontSize="xs"
                                                letterSpacing="wider"
                                                isNumeric
                                            >
                                                Discount
                                            </Th>
                                            {!salesTaxAsLineItem && (
                                                payloadPreview.apHeader.gstHSTTax != null && payloadPreview.apHeader.gstHSTTax + (payloadPreview.apHeader.pstTax ?? 0) !== 0 && payloadPreview.apHeader.tax === 0 ? (
                                                    <>
                                                        <Th textTransform="uppercase" color="gray.500" fontSize="xs" letterSpacing="wider" isNumeric>
                                                            GST/HST
                                                        </Th>
                                                        <Th textTransform="uppercase" color="gray.500" fontSize="xs" letterSpacing="wider" isNumeric>
                                                            PST
                                                        </Th>
                                                    </>
                                                ) : (
                                                    <Th textTransform="uppercase" color="gray.500" fontSize="xs" letterSpacing="wider" isNumeric>
                                                        Tax
                                                    </Th>
                                                )
                                            )}
                                            <Th
                                                textTransform="uppercase"
                                                color="gray.500"
                                                fontSize="xs"
                                                letterSpacing="wider"
                                                isNumeric
                                            >
                                                Total
                                            </Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        <Tr>
                                            <Td fontSize="sm" fontWeight="medium" color="gray.900">{payloadPreview.apHeader.invoiceNo}</Td>
                                            <Td fontSize="sm" fontWeight="medium" color="gray.900">{payloadPreview.apHeader.invoiceDate}</Td>
                                            <Td>
                                                <Box
                                                    as="span"
                                                    display="inline-block"
                                                    px={3}
                                                    py={1.5}
                                                    borderRadius="md"
                                                    border="2px solid"
                                                    borderColor={AP_HEADER_INVOICE_TYPE_CHIP[payloadPreview.apHeader.invoiceType].borderColor}
                                                    bg={AP_HEADER_INVOICE_TYPE_CHIP[payloadPreview.apHeader.invoiceType].bg}
                                                    color={AP_HEADER_INVOICE_TYPE_CHIP[payloadPreview.apHeader.invoiceType].color}
                                                    fontWeight="semibold"
                                                    fontSize="sm"
                                                    cursor="default"
                                                >
                                                    <HStack spacing={2}>
                                                        <Icon
                                                            as={AP_HEADER_INVOICE_TYPE_CHIP[payloadPreview.apHeader.invoiceType].icon}
                                                            boxSize={4}
                                                        />
                                                        <Text fontSize="sm">
                                                            {AP_HEADER_INVOICE_TYPE_CHIP[payloadPreview.apHeader.invoiceType].label}
                                                        </Text>
                                                    </HStack>
                                                </Box>
                                            </Td>
                                            <Td fontSize="sm" fontWeight="medium" color="gray.900" isNumeric>{fmtMoney(payloadPreview.apHeader.invoiceAmount)}</Td>
                                            {!freightAsLineItem && (
                                                <Td fontSize="sm" fontWeight="medium" color="gray.900" isNumeric>{fmtMoney(payloadPreview.apHeader.freight)}</Td>
                                            )}
                                            <Td fontSize="sm" fontWeight="medium" color="gray.900" isNumeric>{fmtMoney(payloadPreview.apHeader.discount)}</Td>
                                            {!salesTaxAsLineItem && (
                                                payloadPreview.apHeader.gstHSTTax != null && payloadPreview.apHeader.gstHSTTax + (payloadPreview.apHeader.pstTax ?? 0) !== 0 && payloadPreview.apHeader.tax === 0 ? (
                                                    <>
                                                        <Td fontSize="sm" fontWeight="medium" color="gray.900" isNumeric>{fmtMoney(payloadPreview.apHeader.gstHSTTax)}</Td>
                                                        <Td fontSize="sm" fontWeight="medium" color="gray.900" isNumeric>{fmtMoney(payloadPreview.apHeader.pstTax ?? 0)}</Td>
                                                    </>
                                                ) : (
                                                    <Td fontSize="sm" fontWeight="medium" color="gray.900" isNumeric>{fmtMoney(payloadPreview.apHeader.tax)}</Td>
                                                )
                                            )}
                                            <Td fontSize="sm" fontWeight="medium" color="gray.900" isNumeric>{fmtMoney(payloadPreview.apHeader.total)}</Td>
                                        </Tr>
                                    </Tbody>
                                </Table>
                                </Box>
                            </Box>
                            <Box
                                mt={5}
                                maxH="420px"
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="md"
                                display="flex"
                                flexDirection="column"
                                overflow="hidden"
                            >
                                <Box flexShrink={0} px={3} pt={2} pb={2} bg="white" borderBottom="1px solid" borderColor="gray.100">
                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800" mb={1}>
                                        AP Distribution lines to submit ({payloadPreview.apDist.length})
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                        Matched lines use their reconciled PO line numbers. For unmatched manual lines, choose a PO from the NEX purchase orders on this repair order.
                                    </Text>
                                </Box>
                                <Box flex="1" minH={0} overflow="auto" px={3} pb={3}>
                                <Table size="sm" variant="simple">
                                    <Thead
                                        position="sticky"
                                        top={0}
                                        zIndex={1}
                                        bg="gray.50"
                                        sx={{
                                            th: {
                                                bg: 'gray.50',
                                                borderBottomWidth: '1px',
                                                color: 'gray.500',
                                                fontSize: 'xs',
                                                letterSpacing: '0.05em',
                                                textTransform: 'uppercase',
                                                boxShadow: '0 1px 0 0 var(--chakra-colors-gray-200)',
                                            },
                                        }}
                                    >
                                        <Tr>
                                            <Th>#</Th>
                                            <Th>Folder No</Th>
                                            <Th minW="220px">PO Header ID</Th>
                                            <Th>PO Line No</Th>
                                            <Th>Description</Th>
                                            <Th isNumeric>Unit List</Th>
                                            <Th isNumeric>Unit Cost</Th>
                                            <Th isNumeric>Units</Th>
                                            <Th isNumeric>Total Cost</Th>
                                            <Th>Manual Line Ind</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {payloadPreview.apDist.map((row, idx) => {
                                            const manualInfo = row.manualLineInd !== 0 ? manualPoByGlobalIndex.get(row.globalIndex) : undefined;
                                            const showPoPicker = Boolean(manualInfo && manualInfo.eligible.length > 0);
                                            const showPoMissing = Boolean(manualInfo && manualInfo.eligible.length === 0);

                                            return (
                                                <Tr key={`${row.globalIndex}-${idx}-${row.description}`}>
                                                    <Td>{idx + 1}</Td>
                                                    <Td>{row.folderNo}</Td>
                                                    <Td verticalAlign="top">
                                                        {showPoPicker && manualInfo ? (
                                                            <Box minW="280px" maxW="420px">
                                                                <Select
                                                                    placeholder="Select PO header…"
                                                                    size="sm"
                                                                    value={
                                                                        Number.isFinite(manualPoHeaderSelections[row.globalIndex])
                                                                            ? String(manualPoHeaderSelections[row.globalIndex])
                                                                            : ''
                                                                    }
                                                                    onChange={(e) => {
                                                                        const v = parseInt(e.target.value, 10);
                                                                        if (Number.isFinite(v)) onManualPoChange(row.globalIndex, v);
                                                                    }}
                                                                >
                                                                    {manualInfo.eligible.map((opt) => (
                                                                        <option key={opt.poHeaderID} value={opt.poHeaderID}>
                                                                            {opt.poHeaderID} — {opt.vendorName} — {opt.orderedDateLabel}
                                                                        </option>
                                                                    ))}
                                                                </Select>
                                                            </Box>
                                                        ) : showPoMissing ? (
                                                            <Text fontSize="xs" color="red.500">
                                                                No purchase orders returned from NEX for this folder
                                                            </Text>
                                                        ) : (
                                                            <Text fontSize="sm">{row.poHeaderID ?? '—'}</Text>
                                                        )}
                                                    </Td>
                                                    <Td>
                                                        {row.poLineNo === null || row.poLineNo === undefined
                                                            ? '—'
                                                            : String(row.poLineNo)}
                                                    </Td>
                                                    <Td>{row.description}</Td>
                                                    <Td isNumeric>{fmtUnitMoney(row.unitList)}</Td>
                                                    <Td isNumeric>{fmtUnitMoney(row.unitCost)}</Td>
                                                    <Td isNumeric>{row.units}</Td>
                                                    <Td isNumeric>{fmtMoney(row.totalCost)}</Td>
                                                    <Td>
                                                        {row.manualLineInd !== 0 ? (
                                                            <Badge colorScheme="yellow">{row.manualLineInd}</Badge>
                                                        ) : (
                                                            <Badge colorScheme="green">0</Badge>
                                                        )}
                                                    </Td>
                                                </Tr>
                                            );
                                        })}
                                    </Tbody>
                                </Table>
                                </Box>
                            </Box>
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <HStack spacing={3}>
                        <Button size="action" variant="outlineGray" onClick={onClose}>
                            Cancel
                        </Button>
                        <Tooltip
                            label={submitDisabledTooltip ?? ''}
                            placement="top"
                            hasArrow
                            isDisabled={!confirmDisabled || submitDisabledTooltip == null}
                        >
                            <Box as="span" display="inline-block">
                                <Button
                                    size="action"
                                    color="white"
                                    bg={ACCENT}
                                    _hover={{ bg: ACCENT_HOVER }}
                                    onClick={onConfirm}
                                    isDisabled={confirmDisabled}
                                    isLoading={isLoading}
                                    loadingText="Submitting..."
                                >
                                    Submit
                                </Button>
                            </Box>
                        </Tooltip>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default InvoiceSubmitConfirmModal;
